import { TemplateFormDialogContents } from "@tellescope/calendar";
import {
  TellescopeModelFromName,
  useCachedTags,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { CalendarEventReminder } from "@tellescope/types-models";
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  Label,
  MultiSelect,
  withKey,
} from "@tellescope/ui";
import { deepEqual } from "@tellescope/utils";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface AppointmentReminderDialogContentsProps {
  template?: TellescopeModelFromName<"calendar_event_templates">;
  templateId?: string;
  onSave?: () => void;
  onCancel?: () => void;
}

export const AppointmentReminderDialogContents = withKey(
  ({ template: providedTemplate, templateId, onSave, onCancel }: AppointmentReminderDialogContentsProps) => {
    const { t } = useTranslation("appointment-reminders");
    const templateQuery = useTellescopeQuery({
      model: "calendar_event_templates",
      method: "getOne",
      request: templateId ?? "",
      enabled: !providedTemplate && !!templateId,
    });
    const template = providedTemplate ?? templateQuery.data;

    const updateTemplateMutation = useTellescopeMutation({
      model: "calendar_event_templates",
      method: "updateOne",
    });

    const [newReminders, setNewReminders] = useState<CalendarEventReminder[]>();
    const resolvedReminders = useMemo(
      () => newReminders ?? template?.reminders ?? [],
      [newReminders, template?.reminders]
    );

    const [newTags, setNewTags] = useState(template?.tags);
    const resolvedTags = useMemo(() => newTags ?? template?.tags ?? [], [newTags, template?.tags]);

    const isDirty = useMemo(() => {
      if (!template) return false;
      if (resolvedReminders.length !== (template.reminders?.length ?? 0)) return true;
      if (
        resolvedReminders.some((reminder, i) => {
          const match = template.reminders?.at(i);
          return !deepEqual(match, reminder);
        })
      )
        return true;
      if (resolvedTags.length !== (template.tags?.length ?? 0)) return true;
      if (resolvedTags.some((tag) => !template.tags?.includes(tag))) return true;
      return false;
    }, [resolvedReminders, resolvedTags, template]);

    const handleSave = useCallback(async () => {
      if (!templateId) return;
      await updateTemplateMutation.mutateAsync(
        {
          id: templateId,
          reminders: resolvedReminders,
          tags: resolvedTags,
          _options: {
            replaceObjectFields: true,
          },
        },
        { onSuccess: onSave }
      );
    }, [onSave, resolvedReminders, resolvedTags, templateId, updateTemplateMutation]);

    const { data: availableTags } = useCachedTags({
      model: "calendar_event_templates",
    });

    return (
      <DialogContent divided data-testid="appointment-reminders-dialog" className="sm:max-w-4xl">
        <DialogHeader className="px-0">
          <h2 className="px-6">
            {template?.title
              ? t("Edit Appointment Type: {{title}}", { title: template.title })
              : t("Edit Appointment Type")}
          </h2>
        </DialogHeader>
        <DialogSection className="space-y-6">
          <TemplateFormDialogContents.RemindersSection
            reminders={resolvedReminders}
            setReminders={setNewReminders}
            limitTypes={["user-notification", "enduser-notification"]}
          />
          <div className="space-y-2">
            <Label htmlFor="tags">{t("Tags")}</Label>
            <MultiSelect
              className="h-auto py-0 w-full"
              value={resolvedTags}
              onValueChange={setNewTags}
              options={Array.from(new Set([...availableTags, ...resolvedTags])).map((tag) => ({
                label: tag,
                value: tag,
              }))}
              placeholder={t("Select tags...")}
              creator={{
                render: (inputValue) => <p>{t("Add '{{tag}}' tag", { tag: inputValue })}</p>,
                transform: (inputValue) => inputValue,
                onCreate: (val) => setNewTags((prev) => [...(prev ?? []), val.value]),
              }}
            />
          </div>
        </DialogSection>
        <DialogFooter className="bg-muted rounded-b-md">
          <Button variant="outline" onClick={onCancel} data-testid="template-form-cancel">
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            className="font-light"
            disabled={!isDirty}
            loading={updateTemplateMutation.isPending}
            data-testid="template-form-submit"
          >
            {t("Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  },
  ({ template, templateId }) => template?.id ?? templateId ?? ""
);
